// Phase 19 · Community V2 · Messages inbox (list + conversation pane)
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, MessageCircle, User, Search, Flag, Paperclip, MoreVertical, Ban, Settings } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import NotificationBell from "../../components/community_v2/NotificationBell";
import MarketplaceEmbedCard from "../../components/community/MarketplaceEmbedCard";
import MiniLandingEmbedCard from "../../components/community/MiniLandingEmbedCard";
import AttachmentPicker from "../../components/community_v2/AttachmentPicker";
import BlockUserModal from "../../components/community_v2/BlockUserModal";
import ReportDmModal from "../../components/community_v2/ReportDmModal";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch (e) { return ""; }
}

function ConversationRow({ conv, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full text-left px-3 py-3 border-b border-[#1A1A1A] transition-colors flex items-center gap-3 ${active ? "bg-[#FFFFFF]/10" : "hover:bg-white/5"}`}
            data-testid={`conv-row-${conv.other_username}`}
        >
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-neutral-900 font-bold flex-shrink-0">
                {conv.other_username?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-neutral-100 truncate">
                        @{conv.other_username || "user"}
                    </p>
                    <span className="text-[10px] text-neutral-500 flex-shrink-0">
                        {fmtTime(conv.last_message_at)}
                    </span>
                </div>
                <p className="text-xs text-neutral-500 truncate">
                    {conv.last_message_preview || "Start the conversation"}
                </p>
            </div>
            {conv.unread_count > 0 && (
                <span className="w-5 h-5 rounded-full bg-[#FAFAFA] text-[10px] font-bold text-white flex items-center justify-center">
                    {conv.unread_count}
                </span>
            )}
        </button>
    );
}

function MessageBubble({ m, isMe, onReport }) {
    return (
        <div
            className={`flex ${isMe ? "justify-end" : "justify-start"} px-4 py-1 group`}
            data-testid={`msg-bubble-${m.id}`}
        >
            {!isMe && (
                <button
                    type="button"
                    onClick={() => onReport?.(m)}
                    className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-[#FAFAFA] mr-1 self-center transition-opacity"
                    title="Report message"
                    data-testid={`msg-report-btn-${m.id}`}
                >
                    <Flag size={12} />
                </button>
            )}
            <div className={`max-w-[70%] rounded-2xl px-3 py-2 ${isMe ? "bg-white text-neutral-900" : "bg-[#1A1A1A] text-neutral-100"}`}>
                {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                {m.message_type === "asset_marketplace" && m.marketplace_preview && (
                    <div className="mt-2">
                        <MarketplaceEmbedCard preview={m.marketplace_preview} />
                    </div>
                )}
                {m.message_type === "mini_landing" && m.mini_landing_preview && (
                    <div className="mt-2">
                        <MiniLandingEmbedCard preview={m.mini_landing_preview} />
                    </div>
                )}
                {m.message_type === "link" && m.embed_url && (
                    <a
                        href={m.embed_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={`text-xs underline mt-1 block ${isMe ? "text-neutral-900/80" : "text-[#CCCCCC]"}`}
                    >
                        {m.embed_url}
                    </a>
                )}
                <p className={`text-[10px] mt-1 ${isMe ? "text-neutral-900/60" : "text-neutral-500"}`}>
                    {fmtTime(m.sent_at)}
                </p>
            </div>
        </div>
    );
}

export default function MessagesInbox() {
    const { user } = useAuth();
    const { username: activeUsername } = useParams();
    const navigate = useNavigate();
    const [conversations, setConversations] = useState([]);
    const [currentConv, setCurrentConv] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [searchUser, setSearchUser] = useState("");
    const [showNewConv, setShowNewConv] = useState(!activeUsername);
    const [showAttachment, setShowAttachment] = useState(false);
    const [showBlock, setShowBlock] = useState(false);
    const [reportMsg, setReportMsg] = useState(null);
    const [showConvMenu, setShowConvMenu] = useState(false);
    const messagesEndRef = useRef(null);
    const pollingRef = useRef(null);

    const loadConversations = useCallback(async () => {
        try {
            const r = await fetch(`${API}/api/community/dm/conversations`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); return; }
            if (!r.ok) return;
            const d = await r.json();
            setConversations(d.items || []);
        } catch {}
    }, []);

    const openConversation = useCallback(async (uname) => {
        if (!uname) return;
        try {
            const r = await fetch(`${API}/api/community/dm/conversations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ target_username: uname }),
            });
            if (!r.ok) {
                if (r.status === 403) toast.error("This user is not accepting messages");
                else if (r.status === 404) toast.error("User not found");
                else toast.error("Cannot open conversation");
                return;
            }
            const conv = await r.json();
            setCurrentConv(conv);
            setShowNewConv(false);
            await loadMessages(conv.id);
            // mark read
            fetch(`${API}/api/community/dm/conversations/${conv.id}/mark-read`, {
                method: "POST", credentials: "include",
            }).catch(() => {});
        } catch { toast.error("Network error"); }
    }, []);

    const loadMessages = useCallback(async (convId) => {
        try {
            const r = await fetch(`${API}/api/community/dm/conversations/${convId}/messages?limit=100`, {
                credentials: "include",
            });
            if (r.ok) {
                const d = await r.json();
                setMessages(d.items || []);
                setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
                }, 50);
            }
        } catch {}
    }, []);

    useEffect(() => { loadConversations(); }, [loadConversations]);

    useEffect(() => {
        if (activeUsername) openConversation(activeUsername);
    }, [activeUsername, openConversation]);

    // Polling messages every 30s when conversation open (Visibility API pause)
    useEffect(() => {
        if (!currentConv) return undefined;
        const poll = () => {
            if (typeof document !== "undefined" && document.hidden) return;
            loadMessages(currentConv.id);
            loadConversations();
        };
        pollingRef.current = setInterval(poll, 30000);
        const onVis = () => { if (!document.hidden) poll(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(pollingRef.current);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [currentConv, loadMessages, loadConversations]);

    const send = async (payload = null) => {
        const bodyPayload = payload || { message_type: "text", body: text.trim() };
        if (!currentConv) return;
        if (bodyPayload.message_type === "text" && !bodyPayload.body) return;
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/dm/conversations/${currentConv.id}/messages`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(bodyPayload),
            });
            if (!r.ok) {
                toast.error(r.status === 429 ? "Slow down" : "Send failed");
                return;
            }
            setText("");
            await loadMessages(currentConv.id);
            await loadConversations();
        } catch { toast.error("Network error"); }
        finally { setBusy(false); }
    };

    const sendAttachment = (item) => {
        if (!item) return;
        const payload = { message_type: item.kind };
        if (item.kind === "asset_marketplace") payload.marketplace_asset_ref = item.ref;
        else if (item.kind === "mini_landing") payload.mini_landing_ref = item.ref;
        send(payload);
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter flex flex-col" data-testid="messages-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/community" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                            <ArrowLeft size={16} /> Community
                        </Link>
                        <span className="text-neutral-600">·</span>
                        <h1 className="text-base font-semibold flex items-center gap-2">
                            <MessageCircle size={16} className="text-[#CCCCCC]" /> Messages
                        </h1>
                    </div>
                    <NotificationBell enabled={!!user} />
                </div>
            </header>

            <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-4">
                {disabled && (
                    <div className="bg-[#FAFAFA]/10 border border-[#FAFAFA]/30 rounded-lg p-4 text-sm text-[#FAFAFA] mb-4">
                        Direct messaging disabled by admin.
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-8rem)]">
                    {/* Conversations sidebar */}
                    <aside className="md:col-span-1 bg-[#111111] border border-[#1A1A1A] rounded-xl overflow-hidden flex flex-col">
                        <div className="p-3 border-b border-[#1A1A1A]">
                            <div className="flex items-center gap-2 text-xs">
                                <input
                                    type="text"
                                    value={searchUser}
                                    onChange={(e) => setSearchUser(e.target.value)}
                                    placeholder="@username to start"
                                    className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1.5 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                                    onKeyDown={(e) => { if (e.key === "Enter") openConversation(searchUser); }}
                                    data-testid="dm-search-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => openConversation(searchUser)}
                                    className="px-2 py-1.5 bg-[#FFFFFF] text-neutral-900 rounded font-semibold hover:opacity-90"
                                    data-testid="dm-start-btn"
                                >
                                    <Search size={14} />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {conversations.length === 0 && (
                                <p className="text-xs text-neutral-500 italic text-center py-8">
                                    No conversations yet
                                </p>
                            )}
                            {conversations.map((c) => (
                                <ConversationRow
                                    key={c.id}
                                    conv={c}
                                    active={currentConv?.id === c.id}
                                    onClick={() => openConversation(c.other_username)}
                                />
                            ))}
                        </div>
                    </aside>

                    {/* Chat pane */}
                    <section className="md:col-span-2 bg-[#111111] border border-[#1A1A1A] rounded-xl flex flex-col overflow-hidden">
                        {!currentConv ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-neutral-500">
                                <MessageCircle size={40} className="mb-2 opacity-40" />
                                <p className="text-sm">Select a conversation or start a new one</p>
                            </div>
                        ) : (
                            <>
                                <div className="p-3 border-b border-[#1A1A1A] flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-neutral-900 font-bold text-sm">
                                        {currentConv.other_username?.[0]?.toUpperCase() || "?"}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <Link
                                            to={`/profile/${currentConv.other_username}`}
                                            className="text-sm font-semibold text-neutral-100 hover:text-[#FFFFFF]"
                                            data-testid="dm-header-username"
                                        >
                                            @{currentConv.other_username}
                                        </Link>
                                    </div>
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setShowConvMenu((o) => !o)}
                                            className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-neutral-500 hover:text-neutral-200"
                                            title="Conversation options"
                                            data-testid="dm-header-menu-btn"
                                        >
                                            <MoreVertical size={16} />
                                        </button>
                                        {showConvMenu && (
                                            <div
                                                className="absolute right-0 mt-1 w-44 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg overflow-hidden z-20"
                                                data-testid="dm-conv-menu"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => { setShowConvMenu(false); setShowBlock(true); }}
                                                    className="w-full text-left px-3 py-2 text-xs text-[#FAFAFA] hover:bg-[#FAFAFA]/10 flex items-center gap-2"
                                                    data-testid="dm-block-btn"
                                                >
                                                    <Ban size={12} /> Block user
                                                </button>
                                                <Link
                                                    to="/community/settings"
                                                    onClick={() => setShowConvMenu(false)}
                                                    className="w-full text-left px-3 py-2 text-xs text-neutral-300 hover:bg-white/5 flex items-center gap-2"
                                                    data-testid="dm-settings-link"
                                                >
                                                    <Settings size={12} /> Privacy settings
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto py-2" data-testid="dm-messages-list">
                                    {messages.map((m) => (
                                        <MessageBubble key={m.id} m={m} isMe={m.sender_id === user?.id} onReport={setReportMsg} />
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>
                                <div className="p-3 border-t border-[#1A1A1A] flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowAttachment(true)}
                                        className="w-9 h-9 rounded-lg border border-[#1A1A1A] text-neutral-400 hover:text-[#FFFFFF] hover:border-[#FFFFFF]/40 flex items-center justify-center flex-shrink-0"
                                        title="Share marketplace asset or mini-landing"
                                        data-testid="dm-attachment-btn"
                                    >
                                        <Paperclip size={16} />
                                    </button>
                                    <input
                                        type="text"
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(); }}
                                        placeholder="Type a message…"
                                        className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                                        data-testid="dm-text-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => send()}
                                        disabled={busy || !text.trim()}
                                        className="px-3 py-2 bg-white text-neutral-900 font-semibold rounded-lg disabled:opacity-40"
                                        data-testid="dm-send-btn"
                                    >
                                        <Send size={16} />
                                    </button>
                                </div>
                            </>
                        )}
                    </section>
                </div>
            </main>

            {showAttachment && (
                <AttachmentPicker
                    onClose={() => setShowAttachment(false)}
                    onPick={sendAttachment}
                />
            )}
            {showBlock && currentConv && (
                <BlockUserModal
                    username={currentConv.other_username}
                    onClose={() => setShowBlock(false)}
                    onBlocked={() => {
                        setCurrentConv(null);
                        setMessages([]);
                        loadConversations();
                        navigate("/messages");
                    }}
                />
            )}
            {reportMsg && (
                <ReportDmModal
                    messageId={reportMsg.id}
                    onClose={() => setReportMsg(null)}
                />
            )}
        </div>
    );
}
